import { useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Button, Card, CopyValue, EmptyState, Eyebrow, Icon, Pill, Skeleton, UiLink } from '@algoboo/ui'
import { ordersQuery, permissionOptionsQuery, portfolioQuery, usePermissionOptions, usePortfolio, walletStatementQuery } from '../lib/api'
import { usePageMeta } from '../lib/meta'
import { SignInPrompt, useSession } from '../components/session'
import { WALLET_STATUS } from '../components/status'
import { QueryError } from '../components/widgets'
import { DepositQr } from '../components/wallet/DepositQr'
import { AuthorizeDialog } from '../components/wallet/AuthorizeDialog'
import { PermissionList } from '../components/wallet/PermissionList'
import { WithdrawCard } from '../components/wallet/WithdrawCard'
import { BalancesCard, LotsCard, OrdersCard, StatementCard } from '../components/wallet/WalletSections'
import { prefetch, prefetchSignedIn } from '../lib/route-data'

/**
 * `/portfolio/wallet`: the account's trading wallet (decision 1 of 30 Sep, lane BOOCLEAN1: one HD-custody wallet per
 * account, so the page names no wallet), with its permissions, orders, statement, balances, lots and withdrawals.
 */
export const Route = createFileRoute('/portfolio/wallet')({
  // the wallet's orders and statement once the portfolio names the wallet
  loader: ({ context: { queryClient: qc } }) =>
    prefetchSignedIn(qc, () => [
      qc.ensureQueryData(portfolioQuery()).then((p) => {
        const address = p.wallet?.wallet.address
        if (address) prefetch(qc.ensureQueryData(ordersQuery(address)), qc.ensureQueryData(walletStatementQuery()))
      }),
      qc.ensureQueryData(permissionOptionsQuery()),
    ]),
  component: WalletPage,
})

function WalletPage() {
  const { me, loading } = useSession()
  usePageMeta('Trading wallet')
  if (loading) return <Skeleton className="h-64 rounded-xl" />
  if (!me) return <SignInPrompt title="Trading wallet" body="Sign in with the wallet that owns your trading wallet to see balances, permissions and the statement." />
  return <Wallet />
}

function Wallet() {
  const q = usePortfolio()
  const options = usePermissionOptions()
  const [authorizing, setAuthorizing] = useState(false)
  if (q.isLoading) return <Skeleton className="h-64 rounded-xl" />
  if (q.error) return <QueryError error={q.error} refetch={() => void q.refetch()} compact={false} />
  const w = q.data?.wallet ?? null
  if (!w) return <EmptyState title="No trading wallet yet" body="Create your trading wallet on the portfolio page first." action={<UiLink href="/portfolio" className="text-[13px] font-medium text-accent hover:underline">Portfolio</UiLink>} />
  const address = w.wallet.address
  const s = WALLET_STATUS[w.wallet.status]
  const available = q.data?.available ?? false
  return (
    <div className="space-y-6">
      <Link to="/portfolio" className="inline-flex items-center gap-1 text-[13px] font-semibold text-ink-3 hover:text-ink">
        <Icon name="chevron-left" size={14} /> Portfolio
      </Link>
      <section className="rh-card rounded-xl p-4 sm:p-6" data-testid="wallet-header" data-status={w.wallet.status}>
        <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Pill tone="brand" icon="wallet">
                Trading wallet
              </Pill>
              <Pill tone={s.tone}>{s.label}</Pill>
            </div>
            <h1 className="rh-h1 mt-2 font-display font-semibold">Your trading wallet</h1>
            <Eyebrow className="mt-4">Deposit address · Robinhood Chain</Eyebrow>
            {address ? <CopyValue value={address} className="mt-1 text-[13px] text-ink" /> : <p className="mt-1 text-[13px] text-ink-3">The trading service is still deriving its address.</p>}
            <p className="mt-2 max-w-md text-[12.5px] text-ink-3">Send ETH or tokens on Robinhood Chain (4663) only. The trading service holds this wallet and its keys for you; only your signature authorizes agents or moves funds out.</p>
          </div>
          {address && <DepositQr address={address} className="self-center md:self-start" />}
        </div>
      </section>

      {!available && (
        <p className="rh-state flex items-center gap-2 rounded-lg px-3 py-2 text-[13px] text-ink-2" role="status" data-testid="portfolio-degraded">
          <Icon name="offline" size={14} className="text-warn" /> The trading service is not answering. Balances, positions and usage are unknown right now, not zero.
        </p>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
        <div className="space-y-6">
          <Card className="overflow-hidden" data-testid="permissions">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-3">
              <h2 className="font-display text-[18px] font-semibold">Authorized agents</h2>
              <Button size="sm" variant="primary" icon="shield" disabled={w.wallet.status !== 'ready'} onClick={() => setAuthorizing(true)} data-testid="open-authorize">
                Authorize agent
              </Button>
            </div>
            <PermissionList permissions={w.permissions} quoteTokens={options.data?.quoteTokens ?? []} />
          </Card>
          {address && <OrdersCard wallet={address} />}
          {address && <StatementCard />}
        </div>
        <div className="space-y-6">
          <BalancesCard balances={w.balances} />
          <LotsCard lots={w.lots} />
          {address && <WithdrawCard wallet={address} balances={w.balances} />}
        </div>
      </div>
      {authorizing && <AuthorizeDialog open onClose={() => setAuthorizing(false)} wallet={w.wallet} />}
    </div>
  )
}
